import { useParams, useNavigate, Link  } from "react-router-dom"
import SlidePreview from "./SlidePreview";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SlideInteractor from "./SlideInteractor";
import { useContext } from 'react';
import { AppContext } from './App';
import '../styles/SlideshowPresentation.css';
import { Avatar } from "ds-form";
import { useRest } from "react-common";
import Loader from "./Loader";


const SlideshowPresentation = ({ presenterView }) => {

    const { t } = useTranslation();
    const { presentationID } = useParams();
    const user = useContext(AppContext);
    const [globalState, setGlobalState] = useState({});
    const ws = useRef(null);
    const [responseTime, setResponseTime] = useState(0);
    const navigate = useNavigate();
    const { getData } = useRest();

    const createSocket = useCallback(() => {
        const newSocket = new WebSocket('wss://halawai-api-dev.italiases.3ds.com/ws/');
        newSocket.onopen = (e) => {
            newSocket.send(JSON.stringify({ module: "Presentation", method: "join", options: { presentationID: presentationID } }))
        }
        newSocket.onclose = () => {
            ws.current = createSocket();
        }
        newSocket.onmessage = (e) => {
            const newState = JSON.parse(e.data).state;
            setGlobalState(newState);    
        }
        return newSocket;
    }, [setGlobalState, presentationID])

    const { activeSlide = {}, 
        isStarted = false, 
        attendees = [], 
        presentedSlideID = null, 
        ranking=[], 
        showRanking=false, 
        isFinished=false, 
        results=null, 
        presentedAt=null,
        timeIsUp=false,
        remainingResponse=[] } = globalState || {};

    const nextSlide = () => {
        ws.current.send(JSON.stringify({ module: "PresentedSlide", method: "next", options: { index: activeSlide.index, presentationID: presentationID }}))
    }

    const previousSlide = () => {
        ws.current.send(JSON.stringify({ module: "PresentedSlide", method: "previous", options: { index: activeSlide.index, presentationID: presentationID }}))
    }

    const toggleRanking = () => {
        ws.current.send(JSON.stringify({ module: "PresentedSlide", method: "toggleRanking", options: { presentationID: presentationID }}))
    }

    const getResponseTime = useCallback(() => {
        const actualTime = new Date().getTime();
        const slidePresentedAt = new Date(presentedAt.date.replace(' ', 'T') + 'Z').getTime();
        const timeToRespond = Math.ceil((slidePresentedAt - actualTime + activeSlide?.configuration?.responseTime * 1000)/1000);
        if(timeToRespond > 0){
            setResponseTime(timeToRespond);
        } else {
            setResponseTime(0);
        }                         
    }, [activeSlide?.configuration?.responseTime, presentedAt])

    const showResponses = useCallback(() => {
        ws.current.send(JSON.stringify({ module: "PresentedSlide", method: "finish", options: { presentationID: presentationID, presentedSlideID: presentedSlideID }}))
    }, [presentationID, presentedSlideID])

    const numberParticipations = useMemo(() => {
        if(activeSlide && (activeSlide.type === 2 || activeSlide.type === 3)){
            let numberResponses = 0;
            remainingResponse.forEach(attendee => {
                if(attendee.remainingResponse === 0){
                    numberResponses++;
                }
            });
            return numberResponses;    
        } 
    }, [remainingResponse, activeSlide])

    useEffect(() => {
        if(presenterView){
            getData("Presentation/" + presentationID, { presenterView: presenterView })
            .catch(err => navigate(`/presentation/${presentationID}`))
        } 
    }, [presenterView, getData, navigate, presentationID])

    useEffect(() => {
        ws.current = createSocket();
        const wsCurrent = ws.current;
        return () => {
            wsCurrent.close();
        }
    }, [createSocket])

    useEffect(() => {
    }, [presentedSlideID])

    useEffect(() => {
        if(presentedAt){
            getResponseTime();    
        }
    }, [getResponseTime, presentedAt])

    return (
        <>
            {!isStarted &&
                <div className="presentationLobby">
                    {presenterView &&
                        <Typography variant="body1">{t("shareCode")} {presentationID}</Typography>
                    }
                    <div className="attendees">
                        <Typography variant="h6">{t("attendee")} ({attendees.length})</Typography>
                        <div className="attendeesAvatars">
                        {attendees && attendees.map((attendee, index) => (
                            <div>
                                <Avatar key={attendee.id} user={{value: attendee.user.login, label: attendee.user.firstName}}/>
                            </div>     
                        ))}
                        {presenterView && attendees.length <= 0 &&
                            <div className="loader">
                                <Loader type="bubbles"/>     
                            </div>
                        }
                        </div>
                    </div>
                    {presenterView &&
                        <Button variant="contained" color="primary" onClick={() => {
                            ws.current.send(JSON.stringify({ module: "Presentation", method: "start", options: { presentationID: presentationID } }))
                        }}>{t("startPresentation")}</Button>
                    }
                    {!presenterView &&
                    <div className="waitPresenterContainer">
                        <Typography variant="body1">{t("waitPresenter")}</Typography>
                        <div className="loader">
                            <Loader type="bubbles"/>     
                        </div> 
                    </div>      
                    }
                </div>
            }

            {isStarted &&
                <>
                    {!isFinished && activeSlide &&
                        <div className="presentationContainer">
                            <div className="slideContainer">
                                <SlidePreview 
                                    slideConfiguration={activeSlide.configuration} 
                                    type={activeSlide.type} 
                                    results={results}
                                    activeSlide={activeSlide} 
                                    responseTime={responseTime} 
                                    timeIsUp={timeIsUp} 
                                    showResponses={showResponses}
                                    ranking={ranking}
                                    showRanking={showRanking}
                                    presentedSlideID={presentedSlideID}
                                />
                                {presenterView &&
                                    <div className="buttonsContainer">
                                        <Button variant="contained" color="primary" onClick={previousSlide}>
                                            {t("previous")}
                                        </Button>
                                        <Button variant="contained" color="primary" onClick={nextSlide}>
                                            {t("next")}
                                        </Button>
                                        {activeSlide.type === 3 &&
                                            <div>
                                                {!activeSlide.configuration.responseTime &&
                                                    <Button variant="outlined" color="primary" onClick={showResponses} disabled={timeIsUp} > 
                                                        {t("showResponses")}
                                                    </Button>
                                                }
                                                <Button variant="text" color="primary" onClick={toggleRanking} disabled={!timeIsUp}> 
                                                    {showRanking ? t("hideRanking") : t("showRanking")}    
                                                </Button>    
                                            </div>    
                                        }
                                        {(activeSlide.type === 2 || activeSlide.type === 3) &&
                                            <div className="numberParticipationsContainer">
                                                <Typography variant="body1">{t('numberParticipations')}{numberParticipations + " / " + attendees.length}</Typography>
                                            </div>   
                                        } 
                                    </div>   
                                }
                            </div>
                            {!presenterView && activeSlide && activeSlide.type !== 4 &&
                                <div className="slideInteractorContainer">
                                    <SlideInteractor
                                        socket={ws.current}
                                        activeSlide={activeSlide}
                                        presentationID={presentationID}
                                        userID={user.id}
                                        timeIsUp={timeIsUp}
                                        remainingResponse={remainingResponse}
                                    />
                                </div>
                            }
                        </div>
                    }

                    {isFinished &&
                        <div className="presentationFinished">
                            <Typography variant="h6">{t('presentationFinished')}</Typography>
                            <div className="linkBackHome">
                                <Link to={`/`}>
                                    <Button variant="outlined" color="primary">{t("backHome")}</Button>     
                                </Link >
                            </div> 
                        </div>
                    }
                </>
            }
        </>
    )
}

export default SlideshowPresentation; 